import { useGlobalContext } from "@/app/context/store";
import Modal from "@/components/shared/modal";
import { hidePost } from "@/lib/api/post";
import { updateAgreedGenerationRules } from "@/lib/api/user";
import Link from "next/link";
import { Dispatch, SetStateAction, useState } from "react";

interface GenerationRulesModalProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  translations: any;
  pressed: "gamba" | "generate";
  handleGamba: () => void;
  handleGenerate: () => void;
}

export default function GenerationRulesModal({
  showModal,
  setShowModal,
  translations,
  pressed,
  handleGamba,
  handleGenerate,
}: GenerationRulesModalProps) {
  const { fetchUser } = useGlobalContext();
  const [isAccepting, setIsAccepting] = useState(false);
  const termsMsg = translations.termsAndRules;
  const termsMsgParts = termsMsg.split("<a>");

  const handleAccept = async () => {
    if (isAccepting) return;

    setIsAccepting(true);
    try {
      await updateAgreedGenerationRules();
      await fetchUser();
      setShowModal(false);
      if (pressed === "gamba") {
        handleGamba();
      } else if (pressed === "generate") {
        handleGenerate();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <Modal showModal={showModal} setShowModal={setShowModal}>
      <div className="w-full overflow-hidden border-0 border-grey-outline bg-crp-grey shadow-xl sm:border-[0.5px] md:max-w-md md:rounded-2xl">
        <div className="flex flex-wrap justify-center border-b-[0.5px] border-grey-outline bg-crp-dark-grey px-6 py-6 text-center text-white">
          <h3 className="text-base font-medium leading-6">{translations.termsAndRulesH1}</h3>
        </div>
        <div className="m-6 space-y-2.5 rounded-lg bg-crp-grey text-center text-sm text-white">
          {termsMsgParts[0] || "By generating content, you accept our"}
          <Link
            className="font-bold text-crp-red"
            href={`${translations.path}terms`}
            target="_blank"
          >
            {termsMsgParts[1] || "Terms of Service"}
          </Link>
          {termsMsgParts[2] || " and "}
          <a
            className="font-bold text-crp-red"
            href={`https://createaiart.zendesk.com/hc/en-us/articles/27705106847387-Community-Guidelines`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {termsMsgParts[3] || "Community Rules"}
          </a>
          {termsMsgParts[4] ||
            " and acknowledge that you are solely responsible for any violations."}
        </div>
        <div className="mx-6 mb-6 flex flex-col items-center gap-2 text-center md:flex-row md:items-center md:justify-center md:gap-6">
          <button
            onClick={handleAccept}
            disabled={isAccepting}
            className="order-1 inline-flex h-10 w-full items-center justify-center rounded-lg border border-grey-outline bg-crp-red px-4 text-sm font-medium text-white transition-all duration-200 md:order-2 md:w-auto"
          >
            {translations.accept}
          </button>
          <button
            onClick={() => setShowModal(false)}
            className="order-2 inline-flex h-10 w-full items-center justify-center rounded-lg border border-grey-outline bg-crp-dark-grey px-4 text-sm font-medium text-white transition-all duration-200 hover:bg-crp-grey md:order-1 md:w-auto"
          >
            {translations.decline}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export const useGenerationRulesModal = (
  translations: any,
  pressed: "gamba" | "generate",
  handleGamba: (justAccepted?: boolean) => void,
  handleGenerate: (justAccepted?: boolean) => void,
) => {
  const [show, setShow] = useState(false);
  return {
    setShowGenerationRulesModal: setShow,
    GenerationRulesModal: () => (
      <GenerationRulesModal
        showModal={show}
        setShowModal={setShow}
        translations={translations}
        pressed={pressed}
        handleGamba={handleGamba}
        handleGenerate={handleGenerate}
      />
    ),
  };
};
